import { useState, useEffect, useCallback } from 'react';

interface ScrollState {
  scrollY: number;
  scrollDirection: 'up' | 'down' | null;
  isAtTop: boolean;
  scrollProgress: number;
  isScrolled: boolean;
}

export function useScrollDirection(threshold = 50): ScrollState {
  const [scrollState, setScrollState] = useState<ScrollState>({
    scrollY: 0,
    scrollDirection: null,
    isAtTop: true,
    scrollProgress: 0,
    isScrolled: false,
  });

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = documentHeight > 0 ? (currentScrollY / documentHeight) * 100 : 0;

    setScrollState((prev) => {
      const direction = currentScrollY > prev.scrollY ? 'down' : currentScrollY < prev.scrollY ? 'up' : prev.scrollDirection;
      
      return {
        scrollY: currentScrollY,
        scrollDirection: direction,
        isAtTop: currentScrollY < 10,
        scrollProgress: Math.min(100, Math.max(0, progress)),
        isScrolled: currentScrollY > threshold,
      };
    });
  }, [threshold]);

  useEffect(() => {
    // Defer initial state read to next frame to avoid forced reflow during render
    const raf = requestAnimationFrame(() => {
      handleScroll();
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return scrollState;
}
